'use client';
import React from 'react';
import { StockSymbol } from '@benzinga/session';
import styled from '@benzinga/themetron';
import { TickerContextProvider } from './context';
import { useTrackSymbol } from '@benzinga/hooks';
import { noop } from '@benzinga/utils';
import { SessionContext } from '@benzinga/session-context';
import { ProUserSettingsManager } from '@benzinga/pro-user-settings';

export interface Props {
  modules: React.ReactNode;
  onClose?: () => void;
  onDialogModeChange?: (isFocused: boolean) => void;
  onMouseEnter?: React.MouseEventHandler<HTMLSpanElement> | undefined;
  onMouseLeave?: React.MouseEventHandler<HTMLSpanElement> | undefined;
  setTooltipHeight?: (tickerHeight: number) => void;
  symbol: StockSymbol;
}

const shouldUpdate = (prevProps: React.PropsWithChildren<Props>, nextProps: React.PropsWithChildren<Props>) => {
  return !(prevProps.symbol !== nextProps.symbol || prevProps.modules !== nextProps.modules);
};

export const TickerHover: React.FC<Props> = React.memo(props => {
  const tickerDivRef = React.useRef<HTMLDivElement | null>(null);
  const [dialogMode, setDialogMode] = React.useState<boolean>(false);
  const session = React.useContext(SessionContext);

  const { onMouseEnter, onMouseLeave } = useTrackSymbol(props.symbol);
  const { showTickerHover } = session.getManager(ProUserSettingsManager).getSettings();
  const setDialogModeCall = React.useCallback(
    (isModel: boolean) => {
      setDialogMode(isModel);
      const onDialogModeChange = props.onDialogModeChange;
      onDialogModeChange?.(isModel);
    },
    [props.onDialogModeChange],
  );

  const updateHeight = React.useCallback(() => {
    const setTooltipHeight = props.setTooltipHeight;
    if (setTooltipHeight && tickerDivRef.current?.clientHeight) {
      setTooltipHeight(tickerDivRef.current.clientHeight);
    }
  }, [props.setTooltipHeight]);

  const handleMouseEnter = React.useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      onMouseEnter();
      props.onMouseEnter?.(e);
    },
    [onMouseEnter, props],
  );

  const handleMouseLeave = React.useCallback(
    (e: React.MouseEvent<HTMLSpanElement>) => {
      onMouseLeave();
      props.onMouseLeave?.(e);
    },
    [onMouseLeave, props],
  );

  React.useEffect(() => {
    onMouseEnter();
    return () => {
      onMouseLeave();
    };
  }, [onMouseEnter, onMouseLeave]);

  if (!showTickerHover) {
    return null;
  }

  return (
    <TickerContextProvider
      onClose={props.onClose ?? noop}
      setDialogMode={setDialogModeCall}
      symbol={props.symbol}
      updateHeight={updateHeight}
    >
      <TickerDiv data-test="ticker-pop-up">
        <TickerHoverDiv
          $dialogMode={dialogMode}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          ref={tickerDivRef}
        >
          {props.modules}
        </TickerHoverDiv>
      </TickerDiv>
    </TickerContextProvider>
  );
}, shouldUpdate);

const TickerDiv = styled.div`
  display: table-row-group;
  cursor: pointer;
  :not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.colors.background};
  }

  :hover {
    /* background: ${props => props.theme.colors.background}; */
  }
`;

const TICKER_HOVER_WIDTH = 360;
const TICKER_HOVER_MIN_HEIGHT = 200;
const TICKER_HOVER_MAX_HEIGHT = '85vh';

const TickerHoverDiv = styled.div<{ $dialogMode: boolean }>`
  background-color: ${props => props.theme.colors.backgroundActive};
  color: ${props => props.theme.colors.foreground};
  border-color: ${props => props.theme.colors.brand};
  border-width: ${props => (props.$dialogMode ? 2 : 0)};
  display: flex;
  flex-direction: column;
  width: ${TICKER_HOVER_WIDTH}px;
  min-height: ${TICKER_HOVER_MIN_HEIGHT}px;
  max-height: ${TICKER_HOVER_MAX_HEIGHT};
  overflow-y: auto;
  white-space: nowrap;
  z-index: 5000;
`;
